/**
 * @file Show a snackbar when the tldraw collaboration status is offline. Also shows a dialog to prevent the user from losing unsaved changes.
 */

import { captureException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { showUnsavedChangesDialog } from '@@/bits/show_unsaved_changes_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useEffect, useRef, useState } from 'react'
import { useCollaborationStatus } from 'tldraw'

/**
 * The collaboration status is often lost for a split second when the sandbox page changes visibility.
 * We don't want to keep showing the snackbar when this happens because it looks buggy, so we have a timeout to delay the snackbar showing.
 */
const OFFLINE_SNACKBAR_TIMEOUT = 3_000
const SENTRY_LOG_TIMEOUT = 30_000

/**
 * Behavior:
 * (1) Connection lost while page hidden -> do not show snackbar -> page is visible -> show snackbar after timeout
 * (2) Connection lost while page visible -> show snackbar after timeout
 * (3) Connection restored while page visible -> hide snackbar
 * (4) Connection restored while page hidden -> hide snackbar
 */
export const useTldrawCollaborationStatus = (): void => {
  const status = useCollaborationStatus()
  const sessionIdRef = useRef('_' + Math.random().toString(36).substring(2, 11))
  const [isPageVisible, setIsPageVisible] = useState(document.visibilityState === 'visible')
  const snackbarRef = useRef('')
  const scheduledShowSnackbarTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const sentryLogTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearSnackbarTimeoutRef = (): void => {
    if (scheduledShowSnackbarTimeoutRef.current != null) {
      clearTimeout(scheduledShowSnackbarTimeoutRef.current)
    }
  }

  const clearSentryLogTimeout = (): void => {
    if (sentryLogTimeoutRef.current != null) {
      clearTimeout(sentryLogTimeoutRef.current)
    }
  }

  const hideSnackbar = (): void => {
    useGlobalSnackbarStore().removeSnackbar(snackbarRef.current)
  }

  const scheduleShowSnackbar = (): void => {
    scheduledShowSnackbarTimeoutRef.current = setTimeout(() => {
      // Show snackbar
      snackbarRef.current = useGlobalSnackbarStore().setSnackbar({
        message: __('Reconnecting... please keep this page open'),
        iconName: 'disconnect_outline',
        persist: true,
      })

      // Schedule Sentry log after 30 seconds
      sentryLogTimeoutRef.current = setTimeout(() => {
        captureException('Sandbox websocket connection did not reconnect within 30 seconds', {
          level: 'warning',
          context: {
            userId: useSurfaceStore().user.id,
            wallId: useSurfaceStore().wallId,
            sessionId: sessionIdRef.current,
          },
        })
      }, SENTRY_LOG_TIMEOUT)
    }, OFFLINE_SNACKBAR_TIMEOUT)
  }

  // Logic that handles snackbar visibility based on collaboration status and page visibility
  useEffect(() => {
    switch (status) {
      case 'online':
        clearSnackbarTimeoutRef()
        clearSentryLogTimeout()
        hideSnackbar()
        break
      case 'offline':
        if (isPageVisible) {
          scheduleShowSnackbar()
        } else {
          clearSnackbarTimeoutRef()
          clearSentryLogTimeout()
        }
        break
      default:
        break
    }
  }, [status, isPageVisible])

  // Show unsaved changes dialog when the connection is lost and the user tries to close the page
  // Always set this immediately since we want to prevent the user from losing unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (status === 'offline') {
        showUnsavedChangesDialog(event)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [status])

  // Set up window visibilitychange listener
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      setIsPageVisible(document.visibilityState === 'visible')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearSnackbarTimeoutRef()
      clearSentryLogTimeout()
    }
  }, [])
}
